import { Board, Direction, Placement } from './types';
import { scoreWord, BoardType } from './scoring';
import { Trie, TrieNode } from './trie';
import { BOARD_SIZE } from './constants';

const SIZE = BOARD_SIZE;

function inBounds(r: number, c: number): boolean {
  return r >= 0 && r < SIZE && c >= 0 && c < SIZE;
}

function isEmpty(board: Board): boolean {
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++)
      if (board[r][c].letter !== null) return false;
  return true;
}

function findAnchors(board: Board, firstMove: boolean): [number, number][] {
  if (firstMove) return [[7, 7]];
  const result: [number, number][] = [];
  const seen = new Set<string>();
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      if (board[r][c].letter !== null) continue;
      for (const [dr, dc] of [[-1,0],[1,0],[0,-1],[0,1]] as [number,number][]) {
        const nr = r + dr;
        const nc = c + dc;
        if (inBounds(nr, nc) && board[nr][nc].letter !== null) {
          const key = `${r},${c}`;
          if (!seen.has(key)) {
            seen.add(key);
            result.push([r, c]);
          }
          break;
        }
      }
    }
  }
  return result;
}

/**
 * Returns the set of letters that can legally be placed at (r,c) given the
 * cross-word constraint (perpendicular to dir), or null if there is no
 * constraint (no adjacent tiles in the perpendicular direction).
 */
function getCrossCheck(
  board: Board,
  r: number,
  c: number,
  dir: Direction,
  trie: Trie,
): Set<string> | null {
  // [dr, dc] are the perpendicular step directions:
  // an 'across' word → scan vertically (dr=1, dc=0) for cross-words;
  // a 'down' word    → scan horizontally (dr=0, dc=1) for cross-words.
  const [dr, dc] = dir === 'across' ? [1, 0] : [0, 1];

  let before = '';
  let pr = r - dr;
  let pc = c - dc;
  while (inBounds(pr, pc) && board[pr][pc].letter) {
    before = board[pr][pc].letter! + before;
    pr -= dr;
    pc -= dc;
  }

  let after = '';
  pr = r + dr;
  pc = c + dc;
  while (inBounds(pr, pc) && board[pr][pc].letter) {
    after += board[pr][pc].letter!;
    pr += dr;
    pc += dc;
  }

  if (!before && !after) return null; // no constraint

  const valid = new Set<string>();
  for (const ch of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
    if (trie.hasWord(before + ch + after)) valid.add(ch);
  }
  return valid;
}

/**
 * Score the cross-word formed when placing `letter` at (r,c) perpendicular
 * to `dir`. `isBlank` indicates the placed tile came from a blank rack tile.
 */
function getCrossScore(
  board: Board,
  r: number,
  c: number,
  letter: string,
  dir: Direction,
  isBlank: boolean,
  boardType: BoardType = 'crossplay',
): number {
  // [dr, dc] are the perpendicular step directions (same convention as getCrossCheck above)
  const [dr, dc] = dir === 'across' ? [1, 0] : [0, 1];
  const perpDir: Direction = dir === 'across' ? 'down' : 'across';

  let before = '';
  let pr = r - dr;
  let pc = c - dc;
  while (inBounds(pr, pc) && board[pr][pc].letter) {
    before = board[pr][pc].letter! + before;
    pr -= dr;
    pc -= dc;
  }

  let after = '';
  pr = r + dr;
  pc = c + dc;
  while (inBounds(pr, pc) && board[pr][pc].letter) {
    after += board[pr][pc].letter!;
    pr += dr;
    pc += dc;
  }

  if (!before && !after) return 0;

  const crossWord = before + letter + after;
  const startR = perpDir === 'down' ? r - before.length : r;
  const startC = perpDir === 'across' ? c - before.length : c;
  const placedIdx = before.length;
  // rackMask: only the newly placed tile; blankMask: set if it's a blank
  const breakdown = scoreWord(
    crossWord, startR, startC, perpDir, board,
    1 << placedIdx,
    isBlank ? (1 << placedIdx) : 0,
    boardType,
  );
  return breakdown.total;
}

/**
 * All state needed for one DFS stack frame.
 *
 * rackMask  – bitmask: bit i set ↔ word[i] came from the rack (not pre-placed)
 * blankMask – bitmask: bit i set ↔ word[i] was played from a blank tile (scores 0)
 *
 * Using integer bitmasks instead of Set<number> eliminates per-step heap
 * allocations in the hot DFS path (no GC pressure from Set cloning).
 */
interface DFSFrame {
  r: number;
  c: number;
  word: string;
  rackLeft: string[];
  rackMask: number;
  blankMask: number;
  hasAnchor: boolean;
  trieNode: TrieNode;
  anchorR: number;
  anchorC: number;
  startR: number;
  startC: number;
  dir: Direction;
  dr: number;
  dc: number;
}

/**
 * Find the best Scrabble placements for `rackStr` on `board`.
 *
 * The solver is iterative (stack-based) and yields to the UI thread every
 * 5 000 steps via setTimeout, so the ActivityIndicator can render.
 *
 * Optimisations:
 *  - Trie-cursor: carries current TrieNode so prefix checks are O(1).
 *  - Cross-check memo: getCrossCheck result cached per (r,c,dir).
 *  - Bitmask tracking: rackMask / blankMask replace Set<number> clones.
 *  - Dedupe key: same letter tried at most once per position.
 *  - Parallel plays: hasAnchor alone guarantees adjacency; no hasBoard check.
 */
export async function solve(board: Board, rackStr: string, trie: Trie, signal?: AbortSignal, boardType: BoardType = 'crossplay'): Promise<Placement[]> {
  const t0 = Date.now();
  const rack = rackStr.toUpperCase().replace(/[^A-Z ]/g, '').split('');
  // Treat uncertain tiles as empty for solving — don't anchor on unconfirmed tiles
  // so the solver only suggests moves using tiles the user has verified.
  const solveBoard: Board = board.map(row =>
    row.map(cell => cell.isUncertain ? { ...cell, letter: null } : cell)
  );
  const firstMove = isEmpty(solveBoard);
  const anchors = findAnchors(solveBoard, firstMove);
  const results: Placement[] = [];
  // Two-level dedup: outer key = word (string, unavoidable), inner key = packed int
  // Encoding: row(4b) | col(4b) | dir(1b) | blankMask(7b) = 16 bits — no string alloc in hot path
  const seen = new Map<string, Set<number>>();
  const packKey = (sR: number, sC: number, d: Direction, bm: number): number =>
    (sR << 12) | (sC << 8) | ((d === 'across' ? 0 : 1) << 7) | (bm & 0x7f);
  // Cache getCrossCheck results — same cell/dir never changes during one solve
  const crossCheckMemo = new Map<string, Set<string> | null>();

  const stack: DFSFrame[] = [];

  for (const [anchorR, anchorC] of anchors) {
    for (const dir of ['across', 'down'] as Direction[]) {
      const [dr, dc] = dir === 'down' ? [1, 0] : [0, 1];

      // Collect start positions up to rack.length steps before the anchor
      let r = anchorR;
      let c = anchorC;
      const starts: [number, number][] = [[r, c]];
      let steps = 0;
      while (steps < rack.length) {
        const nr = r - dr;
        const nc = c - dc;
        if (!inBounds(nr, nc) || solveBoard[nr][nc].letter !== null) break;
        r = nr;
        c = nc;
        steps++;
        starts.push([r, c]);
      }

      for (const [sr, sc] of starts) {
        // Extend start back to include any pre-placed tiles before sr,sc
        let preR = sr - dr;
        let preC = sc - dc;
        let startR = sr;
        let startC = sc;
        while (inBounds(preR, preC) && board[preR][preC].letter) {
          startR = preR;
          startC = preC;
          preR -= dr;
          preC -= dc;
        }

        stack.push({
          r: startR, c: startC,
          word: '',
          rackLeft: [...rack],
          rackMask: 0,
          blankMask: 0,
          hasAnchor: false,
          trieNode: trie.getRoot(),
          anchorR, anchorC, startR, startC, dir, dr, dc,
        });
      }
    }
  }

  const step = () => {
    const frame = stack.pop()!;
    const {
      r, c, word, rackLeft, rackMask, blankMask,
      hasAnchor, trieNode, anchorR, anchorC,
      startR, startC, dir, dr, dc,
    } = frame;

    // Record word if ≥2 letters, adjacent to an anchor, and uses ≥1 rack tile.
    // hasAnchor already guarantees adjacency — removing hasBoard check correctly
    // allows parallel plays (words that form multiple 2-letter cross-words).
    if (word.length >= 2 && hasAnchor && rackMask !== 0) {
      if (trieNode.isEnd) {
        // Word must end cleanly (no adjacent tile extending it)
        if (!inBounds(r, c) || solveBoard[r][c].letter === null) {
          // Two-level dedup: word → Set<packed(row,col,dir,blankMask)>
          // Avoids string template allocation on every DFS hit (hot path)
          const posKey = packKey(startR, startC, dir, blankMask);
          let wordSet = seen.get(word);
          if (!wordSet) { wordSet = new Set(); seen.set(word, wordSet); }
          if (!wordSet.has(posKey)) {
            wordSet.add(posKey);
            const mainBreakdown = scoreWord(
              word, startR, startC, dir, solveBoard, rackMask, blankMask, boardType,
            );
            let crossTotal = 0;
            for (let idx = 0; idx < word.length; idx++) {
              if (!(rackMask >> idx & 1)) continue;
              const lr = startR + (dir === 'down' ? idx : 0);
              const lc = startC + (dir === 'across' ? idx : 0);
              crossTotal += getCrossScore(
                solveBoard, lr, lc, word[idx], dir, (blankMask >> idx & 1) === 1, boardType,
              );
            }
            const total = mainBreakdown.total + crossTotal;
            // tilesUsed: rack tiles in word order; blank tiles shown as lowercase
            const tilesUsed: string[] = [];
            for (let idx = 0; idx < word.length; idx++) {
              if (!(rackMask >> idx & 1)) continue;
              tilesUsed.push((blankMask >> idx & 1) ? word[idx].toLowerCase() : word[idx]);
            }
            results.push({
              word,
              direction: dir,
              row: startR,
              col: startC,
              tilesUsed,
              blankMask,
              score: total,
              scoreBreakdown: { ...mainBreakdown, total },
              crossScore: crossTotal > 0 ? crossTotal : undefined,
            });
          }
        }
      }
    }

    if (!inBounds(r, c)) return;

    const existing = solveBoard[r][c].letter;
    if (existing !== null) {
      // Use tile already on board — O(1) trie step
      const nextNode = Trie.step(trieNode, existing);
      if (nextNode !== null) {
        stack.push({
          r: r + dr, c: c + dc,
          word: word + existing,
          rackLeft,
          rackMask,
          blankMask,
          hasAnchor: hasAnchor || (r === anchorR && c === anchorC),
          trieNode: nextNode,
          anchorR, anchorC, startR, startC, dir, dr, dc,
        });
      }
    } else {
      // Try each rack tile at this empty cell
      const memoKey = `${r},${c},${dir}`;
      let check: Set<string> | null;
      if (crossCheckMemo.has(memoKey)) {
        check = crossCheckMemo.get(memoKey) as Set<string> | null;
      } else {
        check = getCrossCheck(solveBoard, r, c, dir, trie);
        crossCheckMemo.set(memoKey, check);
      }

      const tried = new Set<string>();
      for (let i = 0; i < rackLeft.length; i++) {
        const tile = rackLeft[i];
        const letters = tile === ' '
          ? 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
          : [tile];

        for (const letter of letters) {
          // Dedupe: treat blank-sourced and tile-sourced letters as distinct keys
          // so that a blank playing as 'A' doesn't suppress a real 'A' tile (or vice versa).
          const dedupeKey = tile === ' ' ? `blank:${letter}` : `tile:${letter}`;
          if (tried.has(dedupeKey)) continue;
          tried.add(dedupeKey);

          if (check !== null && !check.has(letter)) continue;

          // O(1) trie transition — prune if no words extend this prefix
          const nextNode = Trie.step(trieNode, letter);
          if (nextNode === null) continue;

          const newRack = [...rackLeft];
          newRack.splice(i, 1);
          const pos = word.length;
          const isBlankTile = tile === ' ';

          stack.push({
            r: r + dr, c: c + dc,
            word: word + letter,
            rackLeft: newRack,
            rackMask: rackMask | (1 << pos),
            blankMask: isBlankTile ? blankMask | (1 << pos) : blankMask,
            hasAnchor: hasAnchor || (r === anchorR && c === anchorC),
            trieNode: nextNode,
            anchorR, anchorC, startR, startC, dir, dr, dc,
          });
        }
      }
    }
  };

  // Cooperative async execution: process 5 000 steps per tick, then yield.
  // setImmediate resolves in the current event loop iteration without timer
  // overhead (~4ms minimum on React Native vs ~0ms for setImmediate).
  const yieldToUI = () => new Promise<void>(r =>
    typeof setImmediate !== 'undefined' ? setImmediate(r) : setTimeout(r, 0),
  );

  while (stack.length) {
    for (let i = 0; i < 5000 && stack.length; i++) step();
    if (signal?.aborted) return results;
    await yieldToUI();
  }

  results.sort((a, b) => b.score - a.score);

  // Keep only the highest-scoring variant of each (word, dir, row, col)
  const deduped = new Map<string, Placement>();
  for (const p of results) {
    const key = `${p.word}|${p.direction}|${p.row}|${p.col}`;
    const existing = deduped.get(key);
    if (!existing || existing.score < p.score) {
      deduped.set(key, p);
    }
  }

  const ms = Date.now() - t0;
  console.log(JSON.stringify({
    event: 'solve_complete', ms, rack: rackStr,
    anchors: anchors.length, results: deduped.size,
  }));

  return Array.from(deduped.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, 100);
}
